/**
 * Buy-In Popup for Poker Reborn
 *
 * Allows player to select buy-in amount when sitting at a table.
 *
 * @see docs/screens/room/popups/BUYIN_POPUP.md - Visual specs, behavior, options
 * @see RebuyPopup - Sister component sharing slider, checkboxes, loading state pattern
 */

import { Container, Graphics, Text } from 'pixi.js';
import { Button, PRIMARY_BUTTON_STYLE, SECONDARY_BUTTON_STYLE } from '@/components/common/Button';
import {
  CHIP_INPUT_GAPS,
  SLIDER_LAYOUT,
  CHECKBOX_LAYOUT,
  BUTTON_LAYOUT,
  calculatePopupSize,
} from './chipInputLayout';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  /** Overlay */
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },

  /** Popup (dimensions calculated dynamically via calculatePopupSize) */
  popup: {
    fill: 0x1a1a2e,
    fillBottom: 0x151525, // Subtle gradient bottom
    stroke: 0x3a3a5e,
    cornerRadius: 16,
  },

  /** Title */
  title: {
    fill: 0xffffff,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Info text */
  infoText: {
    fill: 0x888899,
    fontSize: 13,
    fontFamily: 'Roboto, Arial, sans-serif',
  },

  /** Amount text */
  amountText: {
    fill: 0xffd700,
    fontSize: 36,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },

  /** Layout Y positions (relative to center) - popup-specific only */
  layout: {
    title: -130,
    stakes: -105,
    amount: -55,
    // Shared section uses CHIP_INPUT_GAPS for consistent spacing
  },
};

// =============================================================================
// BUY-IN POPUP
// =============================================================================

export class BuyInPopup extends Container {
  private overlay: Graphics;
  private popup: Container;
  private background: Graphics;
  private titleText: Text;
  private stakeInfoText: Text;
  private amountText: Text;
  private sliderTrack: Graphics;
  private sliderFill: Graphics;
  private sliderHandle: Graphics;
  private sliderMinLabel: Text;
  private sliderMaxLabel: Text;
  private autoRefillCheckbox: Container;
  private autoRefillBox: Graphics;
  private autoRebuyCheckbox: Container;
  private autoRebuyBox: Graphics;
  private confirmButton: Button;
  private cancelButton: Button;

  private minBuyIn: number;
  private maxBuyIn: number;
  private tableMaxBuyIn: number; // Original max from table (before chip limit)
  private currentAmount: number;
  private screenWidth: number;
  private screenHeight: number;
  private popupWidth: number;
  private popupHeight: number;
  private isDragging = false;
  private autoRefill = true;
  private autoRebuy = true;
  private loading = true; // Start in loading state

  /** Callback when confirmed */
  onConfirm: ((amount: number, autoRefill: boolean, autoRebuy: boolean) => void) | null = null;

  /** Callback when cancelled */
  onCancel: (() => void) | null = null;

  /** Callback when loading but player has insufficient chips */
  onInsufficientChips: ((minRequired: number, playerChips: number) => void) | null = null;

  constructor(
    screenWidth: number,
    screenHeight: number,
    options: {
      seat: number;
      minBuyIn: number;
      maxBuyIn: number;
      stakes: { smallBlind: number; bigBlind: number };
    }
  ) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.minBuyIn = options.minBuyIn;
    this.maxBuyIn = options.maxBuyIn;
    this.tableMaxBuyIn = options.maxBuyIn; // Store original max
    this.currentAmount = Math.floor(options.minBuyIn + (options.maxBuyIn - options.minBuyIn) * 0.75);

    // Calculate popup dimensions dynamically
    const topOffset = Math.abs(STYLES.layout.title);
    const bottomY =
      STYLES.layout.amount +
      CHIP_INPUT_GAPS.amountToSlider +
      CHIP_INPUT_GAPS.sliderToLabels +
      CHIP_INPUT_GAPS.labelsToCheckboxes +
      CHIP_INPUT_GAPS.checkboxesToButtons +
      BUTTON_LAYOUT.height;
    const popupSize = calculatePopupSize(topOffset, bottomY);
    this.popupWidth = popupSize.width;
    this.popupHeight = popupSize.height;

    // Create overlay
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointerdown', () => { this.handleCancel(); });
    this.addChild(this.overlay);

    // Create popup container
    this.popup = new Container();
    this.popup.x = screenWidth / 2;
    this.popup.y = screenHeight / 2;
    this.addChild(this.popup);

    // Create background
    this.background = new Graphics();
    this.popup.addChild(this.background);
    this.drawBackground();

    // Stop popup clicks from closing
    this.background.eventMode = 'static';
    this.background.on('pointerdown', (e) => { e.stopPropagation(); });

    // Create title
    this.titleText = new Text({
      text: `Seat ${options.seat + 1} - Buy In`,
      style: STYLES.title,
    });
    this.titleText.anchor.set(0.5);
    this.titleText.y = STYLES.layout.title;
    this.popup.addChild(this.titleText);

    // Create stake info
    this.stakeInfoText = new Text({
      text: `Stakes: $${options.stakes.smallBlind}/$${options.stakes.bigBlind}`,
      style: STYLES.infoText,
    });
    this.stakeInfoText.anchor.set(0.5);
    this.stakeInfoText.y = STYLES.layout.stakes;
    this.popup.addChild(this.stakeInfoText);

    // Create amount display (shows "Loading..." until balance received)
    this.amountText = new Text({
      text: 'Loading...',
      style: { ...STYLES.amountText, fill: 0x888899 }, // Dimmed while loading
    });
    this.amountText.anchor.set(0.5);
    this.amountText.y = STYLES.layout.amount;
    this.popup.addChild(this.amountText);

    // Create slider
    this.sliderTrack = new Graphics();
    this.sliderFill = new Graphics();
    this.sliderHandle = new Graphics();
    this.popup.addChild(this.sliderTrack);
    this.popup.addChild(this.sliderFill);
    this.popup.addChild(this.sliderHandle);
    this.drawSlider();
    this.setupSliderInteraction();

    // Calculate shared section positions using CHIP_INPUT_GAPS
    const amountY = STYLES.layout.amount;
    const sliderY = amountY + CHIP_INPUT_GAPS.amountToSlider;
    const sliderLabelsY = sliderY + CHIP_INPUT_GAPS.sliderToLabels;
    const checkboxY = sliderLabelsY + CHIP_INPUT_GAPS.labelsToCheckboxes;
    const buttonY = checkboxY + CHIP_INPUT_GAPS.checkboxesToButtons;

    // Create slider min/max labels
    const sliderWidth = SLIDER_LAYOUT.width;
    const labelStyle = { ...STYLES.infoText, fontSize: 12 };

    this.sliderMinLabel = new Text({
      text: `$${options.minBuyIn}`,
      style: labelStyle,
    });
    this.sliderMinLabel.anchor.set(0.5);
    this.sliderMinLabel.x = -sliderWidth / 2;
    this.sliderMinLabel.y = sliderLabelsY;
    this.popup.addChild(this.sliderMinLabel);

    this.sliderMaxLabel = new Text({
      text: `$${options.maxBuyIn}`,
      style: labelStyle,
    });
    this.sliderMaxLabel.anchor.set(0.5);
    this.sliderMaxLabel.x = sliderWidth / 2;
    this.sliderMaxLabel.y = sliderLabelsY;
    this.popup.addChild(this.sliderMaxLabel);

    // Create checkboxes (arranged horizontally, centered)
    const { gap: checkboxGap, itemWidth: checkboxWidth } = CHECKBOX_LAYOUT;
    const totalCheckboxWidth = checkboxWidth * 2 + checkboxGap;
    const checkboxStartX = -totalCheckboxWidth / 2;

    // Auto Rebuy checkbox (left)
    this.autoRebuyCheckbox = new Container();
    this.autoRebuyCheckbox.x = checkboxStartX;
    this.autoRebuyCheckbox.y = checkboxY;
    this.autoRebuyBox = this.createCheckbox('Auto Rebuy', () => {
      this.autoRebuy = !this.autoRebuy;
      this.updateCheckbox(this.autoRebuyBox, this.autoRebuy);
    });
    this.autoRebuyCheckbox.addChild(this.autoRebuyBox);
    this.popup.addChild(this.autoRebuyCheckbox);
    this.updateCheckbox(this.autoRebuyBox, this.autoRebuy); // Set initial checked state

    // Auto Refill checkbox (right)
    this.autoRefillCheckbox = new Container();
    this.autoRefillCheckbox.x = checkboxStartX + checkboxWidth + checkboxGap;
    this.autoRefillCheckbox.y = checkboxY;
    this.autoRefillBox = this.createCheckbox('Auto Refill', () => {
      this.autoRefill = !this.autoRefill;
      this.updateCheckbox(this.autoRefillBox, this.autoRefill);
    });
    this.autoRefillCheckbox.addChild(this.autoRefillBox);
    this.popup.addChild(this.autoRefillCheckbox);
    this.updateCheckbox(this.autoRefillBox, this.autoRefill); // Set initial checked state

    // Create action buttons
    const { width: buttonWidth, height: buttonHeight, gap: buttonGap } = BUTTON_LAYOUT;
    const totalButtonsWidth = buttonWidth * 2 + buttonGap;

    // Create cancel button (left)
    this.cancelButton = new Button({
      label: 'Cancel',
      width: buttonWidth,
      height: buttonHeight,
      style: SECONDARY_BUTTON_STYLE,
    });
    this.cancelButton.onClick = () => { this.handleCancel(); };
    this.cancelButton.x = -totalButtonsWidth / 2;
    this.cancelButton.y = buttonY;
    this.popup.addChild(this.cancelButton);

    // Create confirm button (right) - disabled until balance loaded
    this.confirmButton = new Button({
      label: 'Sit Down',
      width: buttonWidth,
      height: buttonHeight,
      style: PRIMARY_BUTTON_STYLE,
    });
    this.confirmButton.onClick = () => { this.handleConfirm(); };
    this.confirmButton.x = -totalButtonsWidth / 2 + buttonWidth + buttonGap;
    this.confirmButton.y = buttonY;
    this.confirmButton.disabled = true; // Disabled until balance loaded
    this.popup.addChild(this.confirmButton);

    // Disable slider interaction while loading
    this.sliderHandle.eventMode = 'none';
    this.sliderTrack.eventMode = 'none';
  }

  /**
   * Get current buy-in amount
   */
  getAmount(): number {
    return this.currentAmount;
  }

  /**
   * Set player's chip balance and enable controls.
   * Called when account balance is received from server.
   * Returns false if player has insufficient chips.
   */
  setBalance(playerChips: number): boolean {
    // Calculate effective max buy-in (limited by player's chips)
    const effectiveMaxBuyIn = Math.min(this.tableMaxBuyIn, playerChips);

    // Check if player has enough chips for minimum buy-in
    if (effectiveMaxBuyIn < this.minBuyIn) {
      if (this.onInsufficientChips) {
        this.onInsufficientChips(this.minBuyIn, playerChips);
      }
      return false;
    }

    // Update max buy-in
    this.maxBuyIn = effectiveMaxBuyIn;

    // Calculate initial amount (75% of range)
    this.currentAmount = Math.floor(this.minBuyIn + (this.maxBuyIn - this.minBuyIn) * 0.75);

    // Update amount display with gold color
    this.amountText.text = this.formatAmount(this.currentAmount);
    this.amountText.style.fill = STYLES.amountText.fill;

    // Update slider max label
    this.sliderMaxLabel.text = `$${this.maxBuyIn}`;

    // Redraw slider with correct values
    this.drawSlider();

    // Enable slider interaction
    this.sliderHandle.eventMode = 'static';
    this.sliderHandle.cursor = 'grab';
    this.sliderTrack.eventMode = 'static';
    this.sliderTrack.cursor = 'pointer';

    // Enable confirm button
    this.confirmButton.disabled = false;

    // Mark as no longer loading
    this.loading = false;

    return true;
  }

  /**
   * Check if popup is still loading balance
   */
  isLoading(): boolean {
    return this.loading;
  }

  /**
   * Resize popup
   */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    // Resize overlay
    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    // Reposition popup
    this.popup.x = width / 2;
    this.popup.y = height / 2;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private drawBackground(): void {
    const { fill, fillBottom, stroke, cornerRadius } = STYLES.popup;
    const width = this.popupWidth;
    const height = this.popupHeight;
    const halfWidth = width / 2;
    const halfHeight = height / 2;

    this.background.clear();

    // Main background with gradient effect (top to bottom)
    this.background.roundRect(-halfWidth, -halfHeight, width, height, cornerRadius);
    this.background.fill({
      color: fill,
      alpha: 1,
    });

    // Bottom gradient overlay for depth (draw a rect clipped by the main rounded rect)
    this.background.rect(-halfWidth, halfHeight - 60, width, 60);
    this.background.fill({
      color: fillBottom,
      alpha: 0.5,
    });

    // Outer border
    this.background.roundRect(-halfWidth, -halfHeight, width, height, cornerRadius);
    this.background.setStrokeStyle({ width: 2, color: stroke });
    this.background.stroke();

    // Subtle inner highlight at top
    this.background.moveTo(-halfWidth + cornerRadius, -halfHeight + 1);
    this.background.lineTo(halfWidth - cornerRadius, -halfHeight + 1);
    this.background.setStrokeStyle({ width: 1, color: 0x4a4a6e, alpha: 0.3 });
    this.background.stroke();
  }

  private drawSlider(): void {
    const { width, height, trackColor, fillColor, handleSize, handleColor } = SLIDER_LAYOUT;
    const sliderY = STYLES.layout.amount + CHIP_INPUT_GAPS.amountToSlider;

    // Calculate fill percentage
    const percentage =
      (this.currentAmount - this.minBuyIn) / (this.maxBuyIn - this.minBuyIn);
    const fillWidth = width * percentage;
    const handleX = -width / 2 + fillWidth;

    // Track background (slightly darker for depth)
    this.sliderTrack.clear();
    this.sliderTrack.roundRect(-width / 2, sliderY - height / 2, width, height, height / 2);
    this.sliderTrack.fill(trackColor);

    // Fill with subtle gradient effect
    this.sliderFill.clear();
    if (fillWidth > 0) {
      this.sliderFill.roundRect(-width / 2, sliderY - height / 2, fillWidth, height, height / 2);
      this.sliderFill.fill(fillColor);
    }

    // Handle with shadow for depth
    this.sliderHandle.clear();
    // Shadow
    this.sliderHandle.circle(handleX + 1, sliderY + 2, handleSize / 2);
    this.sliderHandle.fill({ color: 0x000000, alpha: 0.3 });
    // Handle
    this.sliderHandle.circle(handleX, sliderY, handleSize / 2);
    this.sliderHandle.fill(handleColor);
    this.sliderHandle.setStrokeStyle({ width: 2, color: 0xcccccc });
    this.sliderHandle.stroke();
  }

  private setupSliderInteraction(): void {
    const sliderWidth = SLIDER_LAYOUT.width;

    // Make handle interactive
    this.sliderHandle.eventMode = 'static';
    this.sliderHandle.cursor = 'grab';

    this.sliderHandle.on('pointerdown', () => {
      this.isDragging = true;
      this.sliderHandle.cursor = 'grabbing';
    });

    // Make track interactive for click-to-position
    this.sliderTrack.eventMode = 'static';
    this.sliderTrack.cursor = 'pointer';

    this.sliderTrack.on('pointerdown', (e) => {
      const localPos = e.getLocalPosition(this.popup);
      this.updateSliderFromPosition(localPos.x, sliderWidth);
    });

    // Global pointer move and up
    this.popup.eventMode = 'static';

    this.popup.on('pointermove', (e) => {
      if (this.isDragging) {
        const localPos = e.getLocalPosition(this.popup);
        this.updateSliderFromPosition(localPos.x, sliderWidth);
      }
    });

    this.popup.on('pointerup', () => {
      this.isDragging = false;
      this.sliderHandle.cursor = 'grab';
    });

    this.popup.on('pointerupoutside', () => {
      this.isDragging = false;
      this.sliderHandle.cursor = 'grab';
    });
  }

  private updateSliderFromPosition(x: number, sliderWidth: number): void {
    // Clamp x to slider bounds
    const clampedX = Math.max(-sliderWidth / 2, Math.min(sliderWidth / 2, x));

    // Calculate percentage
    const percentage = (clampedX + sliderWidth / 2) / sliderWidth;

    // Calculate amount
    this.currentAmount = Math.round(
      this.minBuyIn + (this.maxBuyIn - this.minBuyIn) * percentage
    );

    // Update display
    this.amountText.text = this.formatAmount(this.currentAmount);
    this.drawSlider();
  }

  private createCheckbox(label: string, onClick: () => void): Graphics {
    const { size, uncheckedColor, labelColor, fontSize } = CHECKBOX_LAYOUT;
    const container = new Graphics();

    // Draw checkbox box (positioned at origin)
    container.roundRect(0, -size / 2, size, size, 4);
    container.fill(uncheckedColor);
    container.setStrokeStyle({ width: 2, color: 0x5a5a7e });
    container.stroke();

    // Add label
    const labelText = new Text({
      text: label,
      style: {
        fill: labelColor,
        fontSize,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    labelText.x = size + 8;
    labelText.anchor.set(0, 0.5);
    container.addChild(labelText);

    // Make interactive
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.hitArea = { contains: (x: number, y: number) => {
      return x >= 0 && x <= size + 100 && y >= -size / 2 && y <= size / 2;
    }};
    container.on('pointerdown', onClick);

    return container;
  }

  private updateCheckbox(checkbox: Graphics, checked: boolean): void {
    const { size, uncheckedColor, checkedColor, checkmarkColor } = CHECKBOX_LAYOUT;

    // Clear and redraw
    checkbox.clear();
    checkbox.roundRect(0, -size / 2, size, size, 4);
    checkbox.fill(checked ? checkedColor : uncheckedColor);
    checkbox.setStrokeStyle({ width: 2, color: checked ? 0x6abf6a : 0x5a5a7e });
    checkbox.stroke();

    // Draw checkmark if checked
    if (checked) {
      checkbox.setStrokeStyle({ width: 3, color: checkmarkColor });
      checkbox.moveTo(4, 0);
      checkbox.lineTo(size / 3, size / 2 - 5);
      checkbox.lineTo(size - 4, -size / 2 + 5);
      checkbox.stroke();
    }
  }

  private handleConfirm(): void {
    if (this.onConfirm) {
      this.onConfirm(this.currentAmount, this.autoRefill, this.autoRebuy);
    }
    this.destroy();
  }

  private handleCancel(): void {
    if (this.onCancel) {
      this.onCancel();
    }
    this.destroy();
  }

  private formatAmount(amount: number): string {
    return `$${amount.toLocaleString()}`;
  }
}
